import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminDashboard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Download, Trash2, Users, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuth();

  useEffect(() => { fetchSubscribers(); }, []);

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/subscribers`, { headers: getAuthHeader() });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    } finally { setLoading(false); }
  };

  const deleteSubscriber = async (id) => {
    try {
      await axios.delete(`${API_URL}/admin/subscribers/${id}`, { headers: getAuthHeader() });
      setSubscribers(subscribers.filter(s => s.id !== id));
      toast.success('Subscriber removed');
    } catch { toast.error('Failed to remove subscriber'); }
  };

  const exportEmails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/subscribers/export`, { headers: getAuthHeader() });
      const { subscribers: subs, count } = response.data;
      const headers = "Name,Email,Date,Source,Status";
      const rows = subs.map(s =>
        `"${(s.name || '').replace(/"/g, '""')}","${s.email}","${s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : ''}","${s.source || ''}","${s.status}"`
      );
      const csvContent = "data:text/csv;charset=utf-8," + headers + "\n" + rows.join("\n");
      const link = document.createElement('a');
      link.setAttribute('href', encodeURI(csvContent));
      link.setAttribute('download', `subscribers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported ${count} subscriber(s)`);
    } catch { toast.error('Failed to export'); }
  };

  const copyAllEmails = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/subscribers/export`, { headers: getAuthHeader() });
      const emails = response.data.subscribers.map(s => s.email);
      await navigator.clipboard.writeText(emails.join(', '));
      toast.success(`${response.data.count} email(s) copied to clipboard`);
    } catch { toast.error('Failed to copy'); }
  };

  if (loading) return <AdminLayout title="Subscribers"><div className="text-center py-12">Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Newsletter Subscribers">
      <div className="space-y-6">
        {/* Stats & Actions */}
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <Card className="border-0 shadow-md">
            <CardContent className="p-6 flex items-center space-x-4">
              <div className="p-3 bg-sage-100 rounded-xl">
                <Users className="h-6 w-6 text-sage-700" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900" data-testid="subscriber-count">{subscribers.length}</p>
                <p className="text-sm text-gray-500">Total Subscribers</p>
              </div>
            </CardContent>
          </Card>
          <div className="flex gap-3">
            <Button onClick={copyAllEmails} variant="outline" className="border-sage-300 text-sage-700" data-testid="copy-emails-btn">
              <Mail className="mr-2 h-4 w-4" />Copy All Emails
            </Button>
            <Button onClick={exportEmails} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="export-csv-btn">
              <Download className="mr-2 h-4 w-4" />Export CSV
            </Button>
          </div>
        </div>

        {/* Subscribers List */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            {subscribers.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">No subscribers yet</p>
                <p className="text-sm mt-1">Subscribers will appear here when people sign up via the Downloads or Blog pages.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="subscribers-table">
                  <thead>
                    <tr className="border-b bg-sage-50/50">
                      <th className="text-left p-4 text-sm font-semibold text-gray-700">#</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-700">Name</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-700">Email</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-700">Source</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-700">Status</th>
                      <th className="text-left p-4 text-sm font-semibold text-gray-700">Date</th>
                      <th className="text-right p-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscribers.map((sub, index) => (
                      <tr key={sub.id} className="border-b last:border-0 hover:bg-sage-50/30 transition-colors">
                        <td className="p-4 text-sm text-gray-500">{index + 1}</td>
                        <td className="p-4 text-sm font-medium text-gray-900">{sub.name || '-'}</td>
                        <td className="p-4 text-sm text-gray-700">{sub.email}</td>
                        <td className="p-4 text-sm text-gray-500 capitalize">{sub.source || '-'}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${sub.subscribed !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {sub.subscribed !== false ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-gray-500">
                          {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="p-4 text-right">
                          <Button onClick={() => deleteSubscriber(sub.id)} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSubscribers;
